import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ phase: "polling", attempts: 0 });
    const stopRef = useRef(false);
    const { refresh } = useAuth();

    useEffect(() => {
        stopRef.current = false;
        if (!sessionId) {
            setState({ phase: "error", message: "Missing session id" });
            return;
        }

        const poll = async (attempt) => {
            if (stopRef.current) return;
            if (attempt >= 8) {
                setState({ phase: "timeout" });
                return;
            }
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    await refresh();
                    setState({ phase: "paid", data });
                    return;
                }
                if (data.status === "expired") {
                    setState({ phase: "expired" });
                    return;
                }
                setState({ phase: "polling", attempts: attempt + 1 });
                setTimeout(() => poll(attempt + 1), 2000);
            } catch (e) {
                setState({ phase: "error", message: e?.response?.data?.detail || e.message });
            }
        };
        poll(0);

        return () => {
            stopRef.current = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <div className="container-edge py-24 min-h-[60vh] flex items-start justify-center">
            <div data-testid="checkout-card" className="w-full max-w-xl border border-black/15 p-10 bg-white">
                {state.phase === "polling" && (
                    <>
                        <Loader2 className="text-klein animate-spin" size={32} />
                        <h1 className="mt-6 font-heading font-black text-3xl tracking-tighter">Confirming your payment…</h1>
                        <p className="mt-3 text-muted-foreground text-sm">Hang tight. This usually takes a few seconds.</p>
                    </>
                )}
                {state.phase === "paid" && (
                    <>
                        <CheckCircle2 className="text-klein" size={32} />
                        <h1 data-testid="checkout-paid" className="mt-6 font-heading font-black text-3xl tracking-tighter">Payment received.</h1>
                        <p className="mt-3 text-muted-foreground">
                            Your modules are unlocked in your dashboard. Welcome aboard.
                        </p>
                        <Link
                            to="/dashboard"
                            data-testid="goto-dashboard"
                            className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors"
                        >
                            Go to dashboard <ArrowRight size={16} />
                        </Link>
                    </>
                )}
                {(state.phase === "timeout" || state.phase === "expired" || state.phase === "error") && (
                    <>
                        <AlertTriangle className="text-destructive" size={32} />
                        <h1 className="mt-6 font-heading font-black text-3xl tracking-tighter">
                            {state.phase === "expired" ? "Session expired" : "We couldn't confirm yet"}
                        </h1>
                        <p className="mt-3 text-muted-foreground text-sm">
                            {state.phase === "error"
                                ? state.message
                                : "If you completed the payment, refresh in a minute or check your dashboard. Otherwise, please try again."}
                        </p>
                        <div className="mt-8 flex gap-3">
                            <Link to="/courses" className="px-6 py-3 border border-black/20 text-sm font-semibold hover:bg-black hover:text-white transition-colors">
                                Back to courses
                            </Link>
                            <Link to="/dashboard" className="px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors">
                                Open dashboard
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
